import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { Marquee } from "../components/Marquee";
import { CIRCUITS, IMAGES } from "../data/site";

export default function Events() {
  return (
    <div data-testid="events-page">
      <PageHeader
        overline="Events & Operations"
        lines={["Operating where", "America races."]}
        description="ABS LLC runs client programs at the great North American road courses — paddock setup, logistics, and trackside execution on real race weekends. Operations follow client calendars, so we don't publish speculative dates."
        image={IMAGES.heroTrack}
        imageAlt="Race circuit with grandstands during an event weekend"
      />

      <Marquee items={CIRCUITS.map((c) => c.name)} dark />

      <section data-testid="events-circuits" className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Reveal>
            <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">
              Circuits we operate at
            </p>
            <h2 className="font-display mt-5 max-w-2xl text-3xl font-extrabold tracking-tight text-[#111111] sm:text-4xl lg:text-5xl">
              Five paddocks. One standard.
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-px bg-neutral-200 md:grid-cols-2">
            {CIRCUITS.map((c, i) => (
              <div key={c.name} className="group bg-white p-8 transition-colors duration-300 hover:bg-[#111111] md:p-12">
                <Reveal delay={0.05 * i}>
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="font-overline text-[11px] tracking-[0.25em] text-neutral-400 transition-colors duration-300 group-hover:text-neutral-500">
                      {String(i + 1).padStart(2, "0")}
                    </p>
                    <span className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-neutral-500 transition-colors duration-300 group-hover:text-neutral-400">
                      <MapPin className="h-3.5 w-3.5 text-[#e63946]" />
                      {c.region}
                    </span>
                  </div>
                  <h3 className="font-display mt-5 text-2xl font-bold tracking-tight text-[#111111] transition-colors duration-300 group-hover:text-white md:text-3xl">
                    {c.name}
                  </h3>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-600 transition-colors duration-300 group-hover:text-neutral-400">
                    {c.text}
                  </p>
                </Reveal>
              </div>
            ))}
            <Reveal delay={0.28} className="h-full">
              <div className="relative h-full min-h-[280px] overflow-hidden">
                <img
                  src={IMAGES.teamPitwall}
                  alt="ABS LLC crew calling a session from the pit wall"
                  className="img-treatment absolute inset-0 h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                <p className="font-overline absolute bottom-6 left-6 text-[10px] uppercase tracking-[0.3em] text-white">
                  Where you race, we operate
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div data-testid="events-results" className="mt-16">
              <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">
                Recent results
              </p>
              <div className="mt-6 grid gap-5 sm:grid-cols-3">
                <figure className="clip-corner img-treatment overflow-hidden">
                  <img src={IMAGES.teamFirst} alt="Team celebrating in Victory Lane" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                </figure>
                <figure className="clip-corner img-treatment overflow-hidden">
                  <img src={IMAGES.teamSecond} alt="Runner-up trophy at Michelin Raceway Road Atlanta" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                </figure>
                <figure className="clip-corner img-treatment overflow-hidden">
                  <img src={IMAGES.teamThird} alt="Team on the podium at Road Atlanta" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                </figure>
              </div>
              <p className="mt-4 text-xs text-neutral-500">
                Podium finishes with client programs — World Racing League at Michelin Raceway Road Atlanta, July 2026.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-14 border border-neutral-200 bg-[#f8f9fa] p-8 md:p-12">
              <h3 className="font-display text-xl font-bold tracking-tight text-[#111111] md:text-2xl">
                Planning your season?
              </h3>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-600 md:text-base">
                Our calendar is built around client programs rather than fixed
                public dates. Tell us which circuits and classes you're
                targeting, and we'll design the support plan around your season —
                including circuits beyond this list.
              </p>
              <Link
                to="/contact"
                data-testid="events-inquiry-link"
                className="group mt-6 inline-flex items-center gap-2 bg-[#111111] px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#e63946]"
              >
                Discuss your calendar
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand title="Pick your circuits." accent="We'll run the weekends." />
    </div>
  );
}
